import { useState } from 'react';
import { useExercisesByBodyPart } from '../../../hooks/useExercises';
import { useCanAccess, useAILimit } from '../../../hooks/useAccess';
import { useFitnessStore } from '../../../store/fitnessStore';
import { generateWorkoutPlan } from '../../../services/anthropic';
import { WORKOUT_CATEGORIES } from '../../../utils/sportsData';
import ExerciseCard from '../shared/ExerciseCard';
import PaywallBanner from '../shared/PaywallBanner';
import { Sparkles, Search } from 'lucide-react';
import { motion } from 'framer-motion';

export default function WorkoutScreen() {
  const profile = useFitnessStore((s) => s.profile);
  const openModal = useFitnessStore((s) => s.openModal);
  const incrementAIUsage = useFitnessStore((s) => s.incrementAIUsage);
  const canUnlimited = useCanAccess('unlimited_exercises');
  const canAIPlan = useCanAccess('ai_plan_generator');
  const { canUse: canUsePlan, remaining: plansRemaining } = useAILimit('plans');
  const [activeTab, setActiveTab] = useState(WORKOUT_CATEGORIES[0]);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');
  const { data: exercises = [], isLoading } = useExercisesByBodyPart(activeTab.bodyParts[0]);

  async function handleGeneratePlan() {
    if (!canAIPlan) { openModal('upgrade'); return; }
    if (!canUsePlan) { openModal('upgrade'); return; }
    setGenerating(true);
    try {
      const plan = await generateWorkoutPlan({
        profile,
        goal: profile.goals?.[0] || 'strength',
        daysPerWeek: 4,
        equipment: ['barbell', 'dumbbell', 'cable'],
        category: activeTab.label,
      });
      openModal('plan', plan);
      incrementAIUsage('plans');
    } catch {
      alert('Could not generate plan. Check your API key.');
    } finally {
      setGenerating(false);
    }
  }

  const filtered = exercises.filter((e) => !search || e.name?.toLowerCase().includes(search.toLowerCase()));
  const limit = canUnlimited ? 50 : 10;

  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 30, marginBottom: 4 }}>TRAIN</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Your personalised training hub</div>

      <div className="fit-scroll-x" style={{ marginBottom: 16 }}>
        {WORKOUT_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setActiveTab(cat)}
            style={{
              flexShrink: 0, padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 11, fontFamily: 'DM Sans',
              background: activeTab.id === cat.id ? '#E8FF47' : 'rgba(255,255,255,0.07)',
              color: activeTab.id === cat.id ? '#0A0A0B' : 'rgba(255,255,255,0.5)',
            }}
          >
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        {canAIPlan ? (
          <div style={{ background: 'linear-gradient(135deg,rgba(232,255,71,0.06),rgba(10,10,11,0))', border: '1px dashed rgba(232,255,71,0.3)', borderRadius: 14, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>✨ AI Plan Generator</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                Custom {activeTab.label} plan · {plansRemaining} left this month
              </div>
            </div>
            <button
              type="button"
              onClick={handleGeneratePlan}
              disabled={generating || !canUsePlan}
              style={{ background: '#E8FF47', color: '#0A0A0B', border: 'none', borderRadius: 10, padding: '9px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: generating || !canUsePlan ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              {generating ? '⏳ Building...' : <><Sparkles size={12} style={{ display: 'inline', marginRight: 4 }} />Generate</>}
            </button>
          </div>
        ) : (
          <PaywallBanner feature="AI Plan Generator" requiredTier="pro">
            <div style={{ background: '#141416', borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>✨ AI Plan Generator</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Custom plan in seconds</div>
            </div>
          </PaywallBanner>
        )}
      </div>

      <div style={{ position: 'relative', marginBottom: 14 }}>
        <Search size={14} color="rgba(255,255,255,0.3)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search exercises..."
          className="fit-input"
          style={{ paddingLeft: 34 }}
        />
      </div>

      <div className="fit-section-label">{activeTab.label.toUpperCase()} EXERCISES</div>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div className="fit-spinner" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.slice(0, limit).map((ex) => (
            <motion.div key={ex.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <ExerciseCard exercise={ex} compact />
            </motion.div>
          ))}
          {!canUnlimited && exercises.length > 10 && (
            <PaywallBanner feature="full exercise library (unlimited)" requiredTier="pro">
              <ExerciseCard exercise={exercises[10]} compact />
            </PaywallBanner>
          )}
        </div>
      )}
    </div>
  );
}
